import { useEffect, useMemo, useState } from "react";
import { Loader2, RotateCcw, Save, Search } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { LANGUAGES, baseDict, flattenStrings } from "@/i18n";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { TranslateButton, TranslateDialog, useAiTranslate } from "@/components/admin/TranslateDialog";

const btn = "inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-4 py-2 transition-colors disabled:opacity-60";
const SECTIONS = [
  ["meta_title", "Tytuł strony (przeglądarka)"], ["nav", "Menu górne"], ["hero", "Sekcja główna (hero)"], ["marquee", "Pasek przewijany"],
  ["about", "O nas"], ["services", "Oferta"], ["pricing", "Cennik"], ["testimonials", "Opinie — nagłówki"], ["knowledge", "Wiedza"],
  ["contact", "Kontakt"], ["footer", "Stopka"], ["cookies", "Baner cookies"], ["chat", "Czat"], ["quiz", "Dobór pakietu (quiz)"],
  ["order", "Formularz zamówienia"], ["lookup", "Pobieranie danych z GUS"], ["payment", "Strona płatności"], ["docs", "Generator dokumentów"],
  ["upload", "Skrzynka dokumentów"], ["quote", "Formularz wyceny (legalizacja)"], ["guard", "Ochrona antybotowa (Turnstile)"],
  ["errors", "Komunikaty błędów"], ["floating", "Przycisk „Panel klienta”"],
];

function FieldRow({ path, base, value, onChange, onTranslate, translating }) {
  const changed = value !== undefined && value !== base;
  const long = (value ?? base).length > 70 || (value ?? base).includes("\n");
  const cls = `bg-background text-sm ${changed ? "border-brand-teal ring-1 ring-brand-teal/40" : ""}`;
  const key = path.split(".").slice(1).join(".");
  return (
    <div data-testid={`i18n-field-${path}`} className="grid sm:grid-cols-[200px_1fr] gap-2 py-2 border-b border-border/60 last:border-0">
      <div className="min-w-0">
        <code className="font-mono text-[11px] text-muted-foreground break-all">{key || path}</code>
        {changed && (
          <button type="button" data-testid={`i18n-reset-${path}`} onClick={() => onChange(undefined)} className="block mt-1 text-[11px] text-brand-teal hover:underline inline-flex items-center gap-1">
            <RotateCcw size={10} /> przywróć oryginał
          </button>
        )}
        {changed && onTranslate && <TranslateButton small loading={translating} onClick={() => onTranslate([{ path, text: value }])} />}
      </div>
      {long ? (
        <Textarea value={value ?? base} onChange={(e) => onChange(e.target.value)} className={`${cls} min-h-[64px]`} />
      ) : (
        <Input value={value ?? base} onChange={(e) => onChange(e.target.value)} className={cls} />
      )}
    </div>
  );
}

export function TextsAdmin() {
  const [lang, setLang] = useState("pl");
  const [all, setAll] = useState(null);
  const [saved, setSaved] = useState(null);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");
  const ai = useAiTranslate();

  useEffect(() => {
    api.get("/admin/i18n").then((r) => { setAll(r.data); setSaved(JSON.stringify(r.data)); }).catch(() => toast.error("Nie udało się pobrać tekstów."));
  }, []);

  const fields = useMemo(() => flattenStrings(baseDict(lang)), [lang]);
  const overrides = all?.[lang] || {};
  const dirty = all && JSON.stringify(all) !== saved;
  const needle = q.trim().toLowerCase();
  const visible = needle ? fields.filter(([p, v]) => p.toLowerCase().includes(needle) || v.toLowerCase().includes(needle) || (overrides[p] || "").toLowerCase().includes(needle)) : fields;

  const setField = (path, base) => (value) => {
    const next = { ...overrides };
    if (value === undefined || value === base) delete next[path];
    else next[path] = value;
    setAll({ ...all, [lang]: next });
  };

  const save = async () => {
    setSaving(true);
    try {
      await Promise.all(LANGUAGES.map((l) => api.put("/admin/i18n", { lang: l.code, overrides: all[l.code] || {} })));
      setSaved(JSON.stringify(all));
      toast.success("Teksty zapisane. Odśwież stronę główną, aby je zobaczyć.");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Nie udało się zapisać tekstów.");
    } finally {
      setSaving(false);
    }
  };

  if (!all) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-slate-600" /></div>;
  const changedCount = Object.keys(overrides).length;
  const plItems = Object.entries(all.pl || {}).map(([path, text]) => ({ path, text }));
  const applyTranslations = (out) => {
    const next = { ...all };
    for (const [code, vals] of Object.entries(out)) next[code] = { ...(next[code] || {}), ...vals };
    setAll(next);
    ai.setResult(null);
    toast.success("Tłumaczenia wstawione do pól EN/DE/FR/UK/TR. Sprawdź i kliknij „Zapisz teksty”.");
  };

  return (
    <div data-testid="texts-admin" className="space-y-6">
      <div className="bg-card border border-border rounded-2xl p-5 sm:p-6 space-y-4">
        <div>
          <h2 className="font-display font-bold text-lg">Teksty strony</h2>
          <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
            Wszystkie teksty interfejsu w 6 językach. Zmienione pola są podświetlone na morsko; „przywróć oryginał” cofa do tekstu z pliku tłumaczeń.
            Dane kontaktowe, pakiety i opinie edytujesz w zakładce „Treść strony”.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {LANGUAGES.map((l) => {
            const n = Object.keys(all[l.code] || {}).length;
            return (
              <button key={l.code} type="button" data-testid={`i18n-lang-${l.code}`} onClick={() => setLang(l.code)}
                className={`${btn} border ${lang === l.code ? "bg-brand-navy text-white border-brand-navy dark:bg-white dark:text-slate-900" : "border-border hover:bg-muted"}`}>
                {l.label}{n > 0 && <span className="ml-1 text-[10px] opacity-70">({n})</span>}
              </button>
            );
          })}
        </div>
        {lang === "pl" && (
          <div className="flex flex-wrap items-center gap-3 rounded-xl bg-muted/60 p-3">
            <TranslateButton loading={ai.loading} count={plItems.length} onClick={() => ai.run(plItems)} />
            <span className="text-xs text-muted-foreground">AI (GPT) zaproponuje tłumaczenia wszystkich zmienionych polskich tekstów — zobaczysz je do sprawdzenia przed wstawieniem. Pojedyncze pole tłumaczysz linkiem „przetłumacz AI” pod jego kluczem.</span>
          </div>
        )}
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="i18n-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Szukaj tekstu lub klucza…" className="pl-9 bg-background" />
        </div>
      </div>

      <Accordion type="multiple" defaultValue={needle ? SECTIONS.map(([k]) => k) : ["hero"]} key={needle ? "search" : "browse"} className="space-y-3">
        {SECTIONS.map(([key, label]) => {
          const rows = visible.filter(([p]) => p === key || p.startsWith(`${key}.`));
          if (!rows.length) return null;
          const n = rows.filter(([p]) => overrides[p] !== undefined).length;
          return (
            <AccordionItem key={key} value={key} data-testid={`i18n-section-${key}`} className="bg-card border border-border rounded-2xl px-5">
              <AccordionTrigger className="hover:no-underline">
                <span className="font-display font-bold text-base">{label}</span>
                <span className="ml-3 text-xs text-muted-foreground font-normal">{rows.length} pól{n ? ` · ${n} zmienionych` : ""}</span>
              </AccordionTrigger>
              <AccordionContent>
                {rows.map(([p, base]) => <FieldRow key={p} path={p} base={base} value={overrides[p]} onChange={setField(p, base)} onTranslate={lang === "pl" ? ai.run : null} translating={ai.loading} />)}
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>

      <TranslateDialog result={ai.result} onClose={() => ai.setResult(null)} onApply={applyTranslations} />

      <div className="sticky bottom-4 flex justify-end">
        <div className="flex items-center gap-3 bg-card border border-border rounded-full pl-4 pr-2 py-2 shadow-lg">
          <span className="text-xs text-muted-foreground">{LANGUAGES.find((l) => l.code === lang)?.label}: {changedCount} zmian</span>
          {dirty && <span data-testid="i18n-unsaved" className="text-xs text-amber-600 dark:text-amber-400">Niezapisane</span>}
          <button type="button" data-testid="i18n-save-button" onClick={save} disabled={saving || !dirty} className={`${btn} bg-brand-teal text-white hover:bg-brand-tealDark`}>
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Zapisz teksty
          </button>
        </div>
      </div>
    </div>
  );
}
